import { Tab, Tabs, TabList, Menu, Text, Divider } from '@chaser/components';
import { useHistory, useRouteMatch } from 'react-router';
import LoadBoundary from '../LoadBoundary';
import { getFeature, useFeatureConfig, FeatureToken } from '../FeatureGate';
import RoleGate, { hasRole } from '../FeatureGate/RoleGate';
import useUser from '../../hooks/useUser';
import useUserPermissions from '../../hooks/useUserPermissions';
import useShortCode from '../../hooks/useShortCode';
import useOrganisation from '../../hooks/useOrganisation';
import useModuleInfo from '../../hooks/useModuleInfo';
import StretchStack from '../StretchStack';
import useResponsiveValue from '../../hooks/useResponsiveValue';
import { Features, Modules } from '../../util/constants';
import { useStorageState } from '../../hooks/useGlobalState';
import AccessDenied from '../AccessDenied';

function useRoleHiddenSet(tabs) {
  const hasRoleGatedTabs = tabs.some(t => t.requiredRoles?.length);
  const userPermissions = useUserPermissions();
  const shortCode = useShortCode();

  if (!hasRoleGatedTabs) return null;

  const hiddenPaths = new Set();
  for (const tab of tabs) {
    if (tab.requiredRoles?.length && !hasRole(userPermissions, tab.requiredRoles, shortCode)) {
      hiddenPaths.add(tab.path);
    }
  }
  return hiddenPaths;
}

const NavigationTabs = ({
  children,
  tabs,
  defaultIndex = 0,
  basePath = '',
  appearance,
  secondaryDivider = false,
  orientation,
  size,
  onChange,
  menuOnMobile,
  moduleName = Modules.RECEIVABLES,
  ...rest
}) => {
  const [{ features = false }] = useStorageState('admin-settings');
  const history = useHistory();
  const match = useRouteMatch(`${basePath}/:tab`);
  const isMobileMenu = useResponsiveValue([true, false]) && menuOnMobile;
  const featureConfig = useFeatureConfig();
  const user = useUser();
  const roleHiddenSet = useRoleHiddenSet(tabs);
  const org = useOrganisation(null, {
    suspense: false,
    throwOnError: false,
  });
  const { plan, subscription } = useModuleInfo(moduleName, { suspense: false });
  const tabItems = tabs.map(({ title, label, path, feature, hidden, requiredRoles }) => {
    const isAdminFeature = feature === Features.ADMIN_ONLY;
    const isAdminFeatureHidden = isAdminFeature && !features;
    const featureObj = feature
      ? getFeature(featureConfig, feature, user, org, {}, plan, null, subscription)
      : null;
    const canAccess =
      featureObj && !(featureObj.name && (!featureObj.enabled || !featureObj.canAccess));
    const lacksRequiredRole = roleHiddenSet ? roleHiddenSet.has(path) : false;

    return {
      title,
      label,
      path,
      feature: featureObj,
      isAdmin: isAdminFeature,
      requiredRoles,
      hidden: hidden || (featureObj && !canAccess) || isAdminFeatureHidden || lacksRequiredRole,
    };
  });

  const matchedIndex = tabs.findIndex(tab => tab.path === match?.params.tab);
  const index = matchedIndex !== -1 ? matchedIndex : defaultIndex;
  const links = tabItems.map(tab => (tab.path[0] === '/' ? tab.path : `${basePath}/${tab.path}`));

  function handleChange(newIndex) {
    history.push(links[newIndex]);
    if (onChange) onChange(newIndex);
  }

  return (
    <Tabs
      index={index}
      paths={links}
      onChange={handleChange}
      as={StretchStack}
      gap="medium"
      {...rest}
    >
      {!isMobileMenu ? (
        <div>
          <TabList appearance={appearance} orientation={orientation} size={size}>
            {tabItems.map(tab => (
              <Tab
                data-testid={`tab-${tab.title.toLowerCase()}`}
                data-trackingid={`tab-${tab.path}`}
                label={
                  tab.label || (
                    <FeatureToken beta={tab.feature?.beta} promote={tab.feature?.promote} />
                  )
                }
                key={tab.title}
                style={{
                  display: tab.hidden ? 'none' : 'initial',
                  border: tab.isAdmin ? '1px solid red' : undefined,
                }}
              >
                {tab.title}
              </Tab>
            ))}
          </TabList>
          {secondaryDivider && <Divider mt="small" />}
        </div>
      ) : (
        <Menu as="nav">
          {tabItems.map((tab, index) => (
            <Menu.Item
              key={tab.title}
              data-trackingid={`tab-${tab.path}`}
              style={{
                display: tab.hidden ? 'none' : 'initial',
                border: tab.isAdmin ? '1px solid red' : undefined,
              }}
              onClick={() => handleChange(index)}
            >
              <Text
                fontSize="medium"
                trim={false}
                style={{ padding: '8px 0', fontWeight: index === matchedIndex ? '600' : undefined }}
              >
                {tab.title}
              </Text>
            </Menu.Item>
          ))}
        </Menu>
      )}
      <LoadBoundary>
        {(!isMobileMenu || matchedIndex > -1) &&
          (() => {
            const currentTab = tabItems[index];
            const currentRequiredRoles = currentTab?.requiredRoles;

            if (currentRequiredRoles && currentRequiredRoles.length > 0) {
              return (
                <RoleGate roles={currentRequiredRoles} renderGate={<AccessDenied />}>
                  {children}
                </RoleGate>
              );
            }

            return children;
          })()}
      </LoadBoundary>
    </Tabs>
  );
};

export default NavigationTabs;
